import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  HeartHandshake,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { BenefitClaim, ClaimsAnalytics } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const CLAIM_TYPES = [
  "outpatient",
  "inpatient",
  "emergency",
  "dental",
  "vision",
  "maternity",
  "prescription",
  "wellness",
  "other",
] as const;

const CLAIM_STATUSES = [
  "submitted",
  "approved",
  "paid",
  "denied",
  "appealed",
] as const;

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "paid"
      ? "emerald"
      : status === "denied"
      ? "rose"
      : status === "appealed"
      ? "amber"
      : "slate";
  const cls =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-800"
      : tone === "amber"
      ? "bg-amber-100 text-amber-800"
      : tone === "rose"
      ? "bg-rose-100 text-rose-800"
      : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${cls}`}
    >
      {status}
    </span>
  );
}

function NewClaimDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    plan_id: "",
    employee_id: "",
    claim_type: CLAIM_TYPES[0] as string,
    provider_claim_id: "",
    gross_amount: "0",
    employer_paid: "0",
    employee_paid: "0",
    submitted_date: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.createBenefitClaim({
        legal_entity_id: legalEntityId,
        ...form,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("benefits.claim_new", "Record a claim")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!form.plan_id || !form.employee_id}
            className="rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("common.create", "Create")}
          </button>
        </div>
      }
    >
      <div className="space-y-3 p-4 text-sm">
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {t(
            "benefits.claim_record_only",
            "Record-only in v1. HR records the claim status as the employee informs them; actual claim is processed via the provider portal.",
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              Plan ID (UUID)
            </label>
            <input
              value={form.plan_id}
              onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              Employee ID (UUID)
            </label>
            <input
              value={form.employee_id}
              onChange={(e) =>
                setForm({ ...form, employee_id: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.claim_type", "Claim type")}
            </label>
            <select
              value={form.claim_type}
              onChange={(e) =>
                setForm({ ...form, claim_type: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            >
              {CLAIM_TYPES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.claim_provider_id", "Provider claim #")}
            </label>
            <input
              value={form.provider_claim_id}
              onChange={(e) =>
                setForm({ ...form, provider_claim_id: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.claim_gross", "Gross")}
            </label>
            <input
              value={form.gross_amount}
              onChange={(e) =>
                setForm({ ...form, gross_amount: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.claim_emp", "Employer paid")}
            </label>
            <input
              value={form.employer_paid}
              onChange={(e) =>
                setForm({ ...form, employer_paid: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.claim_ee", "Employee paid")}
            </label>
            <input
              value={form.employee_paid}
              onChange={(e) =>
                setForm({ ...form, employee_paid: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.claim_submitted_date", "Submitted date")}
          </label>
          <input
            type="date"
            value={form.submitted_date}
            onChange={(e) =>
              setForm({ ...form, submitted_date: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function AnalyticsCard({
  legalEntityId,
}: {
  legalEntityId: string;
}) {
  const { t } = useTranslation();
  const q = useQuery<ClaimsAnalytics>({
    queryKey: ["benefit-claims-analytics", legalEntityId],
    queryFn: () => api.getBenefitClaimsAnalytics(legalEntityId),
    enabled: !!legalEntityId,
  });
  if (q.isLoading || !q.data) return <Loading />;
  const a = q.data;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div className="rounded-2xl border border-border-subtle bg-white p-3">
        <div className="text-xs uppercase tracking-wide text-text-tertiary">
          {t("benefits.claims_total", "Total claims")}
        </div>
        <div className="mt-1 text-2xl font-semibold text-text-primary">
          {a.total_claims}
        </div>
        <div className="text-xs text-text-tertiary">
          {a.period_from} → {a.period_to}
        </div>
      </div>
      <div className="rounded-2xl border border-border-subtle bg-white p-3">
        <div className="text-xs uppercase tracking-wide text-text-tertiary">
          {t("benefits.claims_total_gross", "Total gross (SAR)")}
        </div>
        <div className="mt-1 font-mono text-2xl font-semibold text-text-primary">
          {a.total_gross}
        </div>
      </div>
      <div className="rounded-2xl border border-border-subtle bg-white p-3">
        <div className="text-xs uppercase tracking-wide text-text-tertiary">
          {t("benefits.claims_avg_cycle", "Avg cycle (days)")}
        </div>
        <div className="mt-1 text-2xl font-semibold text-text-primary">
          {a.average_cycle_days ?? "—"}
        </div>
      </div>
      <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-3">
        <div className="text-xs uppercase tracking-wide text-rose-700">
          {t("benefits.claims_watchlist", "High-claim watchlist")}
        </div>
        <div className="mt-1 text-2xl font-semibold text-rose-800">
          {a.high_claim_watchlist.length}
        </div>
        <div className="text-xs text-rose-700">
          {t("benefits.claims_watchlist_note", "≥4 claims/year")}
        </div>
      </div>
      <div className="rounded-2xl border border-border-subtle bg-white p-3 sm:col-span-2">
        <div className="text-xs uppercase tracking-wide text-text-tertiary">
          {t("benefits.claims_by_provider", "By provider")}
        </div>
        <ul className="mt-1 space-y-0.5 text-xs">
          {a.by_provider.map((p) => (
            <li key={p.provider_name}>
              <span className="font-medium">{p.provider_name}</span>{" "}
              <span className="text-text-tertiary">
                — {p.claim_count} claims · {p.total_gross} SAR
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-border-subtle bg-white p-3 sm:col-span-2">
        <div className="text-xs uppercase tracking-wide text-text-tertiary">
          {t("benefits.claims_plan_util", "Plan utilization")}
        </div>
        <ul className="mt-1 space-y-0.5 text-xs">
          {a.plan_utilization.map((p) => (
            <li key={p.plan_id}>
              <span className="font-medium">{p.plan_code}</span>{" "}
              <span className="text-text-tertiary">
                — {p.claim_count} claims · {p.utilization_pct}% of{" "}
                {p.enrolled_employees} enrolled
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ClaimRow({
  c,
  onUpdate,
}: {
  c: BenefitClaim;
  onUpdate: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-text-primary">
            {c.provider_claim_id ?? c.id.slice(0, 8)}
          </div>
          <div className="text-xs text-text-tertiary">
            {c.claim_type} · {c.submitted_date}
          </div>
        </div>
        <StatusPill status={c.claim_status} />
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">{t("benefits.claim_gross", "Gross")}</dt>
          <dd className="font-mono">{c.gross_amount}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.claim_emp", "Employer")}</dt>
          <dd className="font-mono">{c.employer_paid}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.claim_ee", "Employee")}</dt>
          <dd className="font-mono">{c.employee_paid}</dd>
        </div>
      </dl>
      <div className="mt-3 flex gap-1.5">
        {CLAIM_STATUSES.filter((s) => s !== c.claim_status).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onUpdate({ claim_status: s })}
            className="rounded-lg border border-border-subtle px-2 py-1 text-[10px] capitalize text-text-secondary hover:bg-surface-sunken"
          >
            → {s}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BenefitClaimsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery({
    queryKey: ["benefit-claims", legalEntityId],
    queryFn: () => api.listBenefitClaims(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const upd = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Record<string, unknown>;
    }) => api.updateBenefitClaim(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["benefit-claims"] });
      qc.invalidateQueries({
        queryKey: ["benefit-claims-analytics"],
      });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("benefits.claims_title", "Benefits claims")}
        subtitle={t(
          "benefits.claims_subtitle",
          "Record-only register. HR enters claims as the employee informs them — provider portals process the actual claims.",
        )}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("benefits.claim_new", "Record a claim")}
        </button>
      </div>

      {legalEntityId && <AnalyticsCard legalEntityId={legalEntityId} />}

      <h2 className="mt-4 flex items-center gap-2 text-base font-semibold text-text-primary">
        <HeartHandshake className="h-4 w-4" />
        {t("benefits.claims_list_title", "Recent claims")}
      </h2>

      {list.isLoading && <Loading />}
      {list.isError && <ErrorPanel message={(list.error as Error).message} />}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "benefits.claims_empty",
            "No claims yet. Record one as the employee reports it.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((c) => (
            <ClaimRow
              key={c.id}
              c={c}
              onUpdate={(body) => upd.mutate({ id: c.id, body })}
            />
          ))}
        </div>
      )}

      {createOpen && legalEntityId && (
        <NewClaimDialog
          legalEntityId={legalEntityId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["benefit-claims"] });
            qc.invalidateQueries({
              queryKey: ["benefit-claims-analytics"],
            });
          }}
        />
      )}
    </div>
  );
}
